<template><div><h1 id="verdankungslauf-fur-personalisierte-verdankungen" tabindex="-1"><a class="header-anchor" href="#verdankungslauf-fur-personalisierte-verdankungen"><span>Verdankungslauf für personalisierte Verdankungen</span></a></h1>
<p>Dieses Tutorial ergänzt das <RouteLink to="/manual/customer/verdankungen.html">Tutorial Verdankungen</RouteLink> und zeigt den Prozess des Verdankungslaufs mit dessen Prozessschritten. Siehe Grafik.</p>
<ul>
<li>Schritt 1: Buchung</li>
<li>Schritt 2: Job Verdankungen</li>
<li>Schritt 3: Übersicht/Kontrolle/Dokumente</li>
<li>Schritt 4: Dokumente drucken/verschicken</li>
</ul>
<p><img src="@source/tutorial/i/verdankungslauf/vl_1.png" alt=""></p>
<h2 id="_1-buchung" tabindex="-1"><a class="header-anchor" href="#_1-buchung"><span>1. Buchung</span></a></h2>
<p>Siehe dazu die Tutorials <RouteLink to="/basics/buchhaltung.html">Buchhaltung</RouteLink> und [Buchungen einlesen mit/ohne Referenz](../basics/zahlungen.md</p>
<h2 id="_2-job-verdankungen-vordefinieren" tabindex="-1"><a class="header-anchor" href="#_2-job-verdankungen-vordefinieren"><span>2. Job Verdankungen vordefinieren</span></a></h2>
<p>Der Job Verdankungen ist im Voraus zu definieren</p>
<ul>
<li>Definieren, welche Zahlungen zu verdanken sind</li>
<li>Definieren, welche Kanäle benutzt werden sollen</li>
<li>Die Struktur der Verdankungen definieren</li>
</ul>
<h3 id="_2-1-zahlungen-bestimmen-die-zu-verdanken-sind" tabindex="-1"><a class="header-anchor" href="#_2-1-zahlungen-bestimmen-die-zu-verdanken-sind"><span>2.1 Zahlungen bestimmen, die zu verdanken sind</span></a></h3>
<p>Es muss zu Beginn definiert werden, welche Zahlungen zu verdanken sind. Dazu gibt es folgende Kriterien:</p>
<ul>
<li>Ziel den Zahlungen
<ul>
<li>Spende oder Zusage</li>
<li>zweckbestimmte oder allgemeine Spende</li>
</ul>
</li>
<li>Allgemeiner oder individueller Text</li>
<li>Wert der Zahlungen</li>
<li>Personen im Inland oder Ausland</li>
<li>Frequenz der Zahlungen
<ul>
<li>Erste Zahlung, erste Zahlung im Kalenderjahr, zweite, dritte etc. Spende</li>
<li>Spenden pro Person einzeln verdanken</li>
<li>Spenden pro Person zusammen verdanken</li>
</ul>
</li>
</ul>
<h3 id="_2-2-kanale-definieren" tabindex="-1"><a class="header-anchor" href="#_2-2-kanale-definieren"><span>2.2 Kanäle definieren</span></a></h3>
<p>Für Verdankungen lassen sich verschiedene Kanäle benutzen. Nach den individuellen Kriterien der User ist eine Auswahl vorzunehmen</p>
<ul>
<li>Post – Standardkanal. Ab welchem Betrag und in welchem Zusammenhang ist eine postalische Verdankung angezeigt.</li>
<li>E-Mail – wann ist dieser Kanal als Alternative zur postalischen Verdankung zu bevorzugen?</li>
<li>Anruf – z. B. lässt sich eine grössere Summe angemessener per Telefon oder mit einer handschriftlichen Dankeskarte verdanken?</li>
</ul>
<h3 id="_2-3-struktur-definieren" tabindex="-1"><a class="header-anchor" href="#_2-3-struktur-definieren"><span>2.3 Struktur definieren</span></a></h3>
<p>Für eine bessere Übersicht ist es gut, eine sogenannte Baumstruktur zu verwenden. Die Struktur sollte auf der Grundlage der Auswahlkriterien aus Punkt 2.1. ausgewählt werden.</p>
<h4 id="beispiel" tabindex="-1"><a class="header-anchor" href="#beispiel"><span>Beispiel</span></a></h4>
<p><img src="@source/tutorial/i/verdankungslauf/vl_2.png" alt=""></p>
<ol>
<li>
<p>Ausschlüsse – Zahlungen, die nicht verdankt werden sollen, z. B. bei speziellen Ereignissen oder ausdrücklichem Verzicht auf Verdankungen durch die Spendenden</p>
</li>
<li>
<p>Aufteilung in Spenden und Zusagen</p>
</li>
</ol>
<ul>
<li>
<p>Spenden</p>
<ul>
<li>Zweckgebundene – Projekt 1
<ul>
<li>E-Mail erste Spende</li>
<li>Post erste Spende</li>
<li>E-Mail ab zweite Spende</li>
<li>Post ab zweite Spende</li>
<li>Restliche – als Kontrolle, ob ein Kriterium bei der vorherigen Filterung vergessen wurde.</li>
</ul>
</li>
<li>Zweckgebundene – Projekt 2</li>
<li>Zweckgebundene – Projekt 3</li>
<li>Allgemeine Spende <img src="@source/tutorial/i/verdankungslauf/vl_3.png" alt=""></li>
</ul>
</li>
<li>
<p>Zusagen</p>
<ul>
<li>Mitgliedschaft</li>
<li>Patenschaft</li>
</ul>
</li>
</ul>
<ol start="3">
<li>Restliche – hier verbleiben die Zahlungen, die sich nicht in den vorherigen Baum einordnen lassen. Optimal ist es, wenn dieser Knoten keine Zahlungen enthält. Andernfalls können wir im Nachhinein die vorherigen Kriterien neu bewerten, z. vergessene neue Ereignisse aus einem neuen Mailing erfassen.</li>
</ol>
<p><img src="@source/tutorial/i/verdankungslauf/vl_4.png" alt=""></p>
<h2 id="_3-ubersicht-kontrolle" tabindex="-1"><a class="header-anchor" href="#_3-ubersicht-kontrolle"><span>3. Übersicht/Kontrolle</span></a></h2>
<p>Dieser Schritt beinhaltet die Erstellung der Dokumente und die Kontrolle, für welche Personen/Profile welche Dokumente im Verdankungslauf erstellt werden.</p>
<h3 id="_3-1-exkurs-–-dokumente-erstellen" tabindex="-1"><a class="header-anchor" href="#_3-1-exkurs-–-dokumente-erstellen"><span>3.1 Exkurs – Dokumente erstellen</span></a></h3>
<p>Siehe auch unter <RouteLink to="/basics/document-output.html">Document-Output</RouteLink></p>
<p>Bei der Gestaltung von Dokumenten sollte versucht werden, die Anzahl der unterschiedlichen Dokumente nach Struktur und nicht nach Inhalt zu minimieren. Gestaltung und Definition eines neuen Dokuments sind komplexer und zeitaufwändiger.</p>
<p>Die Dokument-Struktur der Verdankungen sollte möglichst immer dieselbe ist, z. B.</p>
<ul>
<li>Seiten- und Textlayout</li>
<li>Platzierung des Logos</li>
<li>Platzierung der Adresse des Absenders</li>
<li>die Kopf- und Fußzeile die gleichen Teile enthalten</li>
</ul>
<p>Die Textinhalte sind variabel.</p>
<ul>
<li>Art Spende / Zusage</li>
<li>Projektspezifischer Text</li>
<li>Unterschriften der zuständigen Benutzer</li>
</ul>
<p>So lässt sich ein Dokument erstellen mit verschiedenen Texten, die gemäss Dokumentencode ausgewählt werden. Es lassen sich damit auch die späteren potenziellen Anpassungen minimalisieren, z. B. bei einer neuen Unterschrift.</p>
<p><img src="@source/tutorial/i/verdankungslauf/vl_5.png" alt=""></p>
<p><img src="@source/tutorial/i/verdankungslauf/vl_6.png" alt=""></p>
<p><img src="@source/tutorial/i/verdankungslauf/vl_7.png" alt=""></p>
<h2 id="_3-2-aufbereitung" tabindex="-1"><a class="header-anchor" href="#_3-2-aufbereitung"><span>3.2 Aufbereitung</span></a></h2>
<p>Der Verdankungslauf besteht aus drei Schritten.</p>
<ol>
<li>Verdankungen aufbereiten – einzelne Zahlungen sind in den verschiedenen Knoten in der Verdankungssteuertabelle selektiert</li>
<li>Dokumente erstellen</li>
<li>Dokumente drucken bzw. als E-Mail senden</li>
</ol>
<p><img src="@source/tutorial/i/verdankungslauf/vl_8.png" alt=""></p>
<p>Nach jeder Änderung der Parametrisierung oder Änderung der Dokumente ist es ratsam, den gesamten Prozess mehrmals in Teilen durchlaufen zu lassen und nach jedem Teil die Korrektheit des Schrittes zu überprüfen.</p>
<ul>
<li>Nach Punkt 1. kontrollieren, ob die Zahlungen richtig selektiert worden sind.</li>
<li>Nach Punkt 2. kontrollieren, ob bei jeder Position das passende Dokument erstellt worden ist.</li>
<li>Anschliessend lassen sich die Dokumente drucken oder per E-Mail senden.</li>
</ul>
<h2 id="_4-dokumente-drucken-und-oder-verschicken" tabindex="-1"><a class="header-anchor" href="#_4-dokumente-drucken-und-oder-verschicken"><span>4.Dokumente drucken und/oder verschicken</span></a></h2>
<p>Wenn die Parametrisierung und die Dokumente richtig definiert sind und der gesamte Prozess mehrfach geprüft ist, kann der gesamte Prozess als Ganzes gestartet werden ohne Unterbrechungen.</p>
<p>Empfehlung:Das Ergebnis nach jedem Vorgang zu überprüfen.</p>
<ul>
<li>Sind die E-Mails verschickt worden?</li>
<li>Gibt es ungedeckte Zahlungen im Rest?</li>
</ul>
<p>Alle Spenden bzw. die verdankt worden sind, erhalten zu ihrer Historie automatisch ein entsprechendes Verdankungsereignis.</p>
</div></template>


